import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { useListStatePersistence } from '@/hooks/useListStatePersistence';
import {
  ShoppingBag,
  Search,
  Filter,
  MapPin,
  Plus,
  Loader2,
  Tag,
  Heart,
  Package,
  DollarSign,
  Check
} from 'lucide-react';

const API = ''; // Use relative URL for same-origin API calls

const MarketplacePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    condition: '',
    city: '',
    is_free: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState({
    categories: [],
    conditions: []
  });
  const [total, setTotal] = useState(0);
  const headerRef = useRef(null);

  // Cache list state across route navigation so back from a listing detail
  // lands the user instantly on the same list at the page header.
  useListStatePersistence({
    key: 'marketplace',
    state: { listings, total, searchTerm, filters },
    enabled: !loading && listings.length > 0,
    onRestore: (cached) => {
      if (Array.isArray(cached.listings)) setListings(cached.listings);
      if (typeof cached.total === 'number') setTotal(cached.total);
      if (typeof cached.searchTerm === 'string') setSearchTerm(cached.searchTerm);
      if (cached.filters) setFilters(cached.filters);
      setLoading(false);
      requestAnimationFrame(() => {
        headerRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
      });
    },
  });

  useEffect(() => {
    fetchCategories();
    fetchListings();
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/marketplace/categories`);
      setCategories(res.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filters.category) params.append('category', filters.category);
      if (filters.condition) params.append('condition', filters.condition);
      if (filters.city) params.append('city', filters.city);
      if (filters.is_free === 'true') params.append('is_free', 'true');

      const res = await axios.get(`${API}/api/marketplace?${params.toString()}`);
      setListings(res.data.listings || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchListings();
  };

  const formatPrice = (price, currency, isFree, isNegotiable) => {
    if (isFree) return 'FREE';
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0
    }).format(price);
    return isNegotiable ? `${formatted} (Negotiable)` : formatted;
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'New': return 'bg-green-100 text-green-700';
      case 'Like New': return 'bg-blue-100 text-blue-700';
      case 'Good': return 'bg-amber-100 text-amber-700';
      case 'Fair': return 'bg-orange-100 text-orange-700';
      case 'For Parts': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary/5 via-accent/5 to-primary/10 py-12 md:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <ShoppingBag className="w-4 h-4" />
                Expat Marketplace
              </div>
              <h1 className="text-4xl md:text-5xl font-black mb-4" ref={headerRef}>
                Buy & Sell with <span className="text-primary">Fellow Expats</span>
              </h1>
              <p className="text-muted-foreground text-lg mb-8">
                Find great deals on furniture, electronics, and more from trusted community members.
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="bg-white rounded-2xl p-4 shadow-lg border-2 border-border">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Search items..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-12 h-12 text-lg rounded-xl border-2"
                      data-testid="marketplace-search-input"
                    />
                  </div>
                  <div className="flex-1 md:flex-none md:w-48 relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="City/Location"
                      value={filters.city}
                      onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                      className="pl-12 h-12 text-lg rounded-xl border-2"
                      data-testid="marketplace-city-input"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 px-6 rounded-xl border-2"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Filter className="w-5 h-5 mr-2" />
                    Filters
                  </Button>
                  <Button type="submit" className="h-12 px-8 rounded-xl font-bold" data-testid="marketplace-search-btn">
                    Search
                  </Button>
                </div>

                {/* Expanded Filters */}
                {showFilters && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
                    <Select
                      value={filters.category}
                      onValueChange={(value) => setFilters({ ...filters, category: value === 'all' ? '' : value })}
                    >
                      <SelectTrigger className="rounded-lg">
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {categories.categories?.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      value={filters.condition}
                      onValueChange={(value) => setFilters({ ...filters, condition: value === 'all' ? '' : value })}
                    >
                      <SelectTrigger className="rounded-lg">
                        <SelectValue placeholder="Condition" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any Condition</SelectItem>
                        {categories.conditions?.map(cond => (
                          <SelectItem key={cond} value={cond}>{cond}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      value={filters.is_free}
                      onValueChange={(value) => setFilters({ ...filters, is_free: value })}
                    >
                      <SelectTrigger className="rounded-lg">
                        <SelectValue placeholder="Price" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Prices</SelectItem>
                        <SelectItem value="true">Free Items Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-bold">{total} Items Available</h2>
              <p className="text-muted-foreground">From the expat community</p>
            </div>
            <Link to="/marketplace/create">
              <Button className="font-bold rounded-xl" data-testid="create-listing-btn">
                <Plus className="w-5 h-5 mr-2" />
                Sell Something
              </Button>
            </Link>
          </div>

          {/* Listings Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : listings.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
              <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">No Items Found</h3>
              <p className="text-muted-foreground mb-6">
                {searchTerm || Object.values(filters).some(v => v)
                  ? 'Try adjusting your search filters'
                  : 'Be the first to list an item!'}
              </p>
              <Link to="/marketplace/create">
                <Button className="rounded-xl font-bold">
                  <Plus className="w-5 h-5 mr-2" />
                  Sell Something
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {listings.map(listing => (
                <Link 
                  key={listing.listing_id} 
                  to={`/marketplace/${listing.listing_id}`}
                  className="group"
                  data-testid={`listing-card-${listing.listing_id}`}
                >
                  <div className="bg-white rounded-2xl border-2 border-border overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all">
                    {/* Image */}
                    <div className="relative aspect-square bg-gradient-to-br from-primary/20 to-accent/20">
                      {listing.images?.[0] ? (
                        <img 
                          src={listing.images[0]} 
                          alt={listing.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-12 h-12 text-primary/40" />
                        </div>
                      )}
                      {/* Badges */}
                      <div className="absolute top-2 left-2 flex flex-col gap-1">
                        {listing.is_free && (
                          <span className="px-2 py-1 rounded-full text-xs font-bold bg-green-500 text-white">
                            FREE
                          </span>
                        )}
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${getConditionColor(listing.condition)}`}>
                          {listing.condition}
                        </span>
                      </div>
                      <button className="absolute top-2 right-2 p-2 bg-white/80 rounded-full hover:bg-white transition-colors">
                        <Heart className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>

                    {/* Content */}
                    <div className="p-3">
                      {/* Price */}
                      <div className="flex items-center gap-1 mb-1">
                        {listing.is_free ? (
                          <span className="text-lg font-black text-green-600">FREE</span>
                        ) : (
                          <span className="text-lg font-black text-primary">
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: listing.price_currency || 'USD',
                              maximumFractionDigits: 0
                            }).format(listing.price)}
                          </span>
                        )}
                        {listing.is_negotiable && !listing.is_free && (
                          <span className="text-xs text-muted-foreground">(OBO)</span>
                        )}
                      </div>

                      {/* Title */}
                      <h3 className="font-bold text-sm mb-1 group-hover:text-primary transition-colors line-clamp-2">
                        {listing.title}
                      </h3>

                      {/* Location & Category */}
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate">{listing.city}</span>
                        <span className="mx-1">•</span>
                        <Tag className="w-3 h-3" />
                        <span className="truncate">{listing.category}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MarketplacePage;
